<template>
  <v-container fluid>
    <v-flex xs12 justify-center align-center style="height: 100%;" v-if="items.length === 0 && searchText === ''">
      <v-img src="/empty_announcement.webp" class="center" style="height: 300px;width: 450px"></v-img>
      <v-alert
        shaped
        text
        type="info"
        max-width="400px"
        class="center"
      >{{ $t('announce.emptyPlaceholder') }}</v-alert>
    </v-flex>
    <v-flex justify-center align-center class="d-flex flex-column" v-else>
      <v-card-title style="width:100%;">
        <v-icon>mdi-bullhorn</v-icon>&nbsp;
        {{ $t('announce.menuTitle') }}
      </v-card-title>
      <v-card v-if="!$vuetify.breakpoint.xs">
        <v-data-table
          ref="announcementDataTable"
          :loading="loading"
          :loading-text="$t('announce.loading')"
          :headers="headers"
          :no-data-text="$t('announce.tip.noData')"
          :options.sync="pagination"
          :server-items-length="announcementPagination.total"
          :items-per-page="defaultItemPerPage"
          :footer-props="footerProps"
          :disable-sort="true"
          :items="items"
          height="calc(95vh - 56px - 56px)"
          style="max-width: 1400px;"
          class="stripped-table"
          id="announcements-iterator">
          <template v-slot:[`item.volumeGainDb`]="{ item }">
            {{ (item.volumeGainDb !== '' && item.volumeGainDb !== null) ? `${item.volumeGainDb} DB` : '' }}
          </template>
          <template v-slot:[`item.textType`]="{ item }">
            {{ item.textType ? $t(`announce.columnRenderer.channelTypes.${item.textType}`) : '' }}
          </template>
          <template v-slot:[`item.speed`]="{ item }">
            {{ item.speed ? `x${item.speed}` : '' }}
          </template>
          <template v-slot:[`item.formattedPostDest`]="{ item }">
            <v-icon v-if="item.formattedPostDest" color="green">mdi-check</v-icon>
            <v-icon v-else color="red">mdi-close</v-icon>
          </template>
          <template v-slot:[`item.textToSpeech`]="{ item }">
            <span>
              <span v-if="item.textToSpeech && item.textToSpeech.length > TTSTextLength">
                {{ item.textToSpeech.slice(0, TTSTextLength) + '...' }}
                <v-menu bottom offset-y max-width="400px" :close-on-content-click="false">
                  <template v-slot:activator="{ on, attrs }">
                    <v-btn x-small icon v-bind="attrs" v-on="on">
                      <v-icon small>mdi-eye</v-icon>
                    </v-btn>
                  </template>
                  <v-card style="max-width: 400px; white-space: pre-line;">
                    <v-card-text>{{ item.textToSpeech }}</v-card-text>
                    <v-card-actions class="justify-end">
                      <v-btn x-small text
                        :disabled="copyButtonDisabled[item.id]"
                        @click.stop="copy(item.textToSpeech, item.id)">
                        <v-icon left small>mdi-content-copy</v-icon>
                        {{ copyButtonText[item.id] || $t('announce.columnRenderer.copy') }}
                      </v-btn>
                    </v-card-actions>
                  </v-card>
                </v-menu>
              </span>
              <span v-else>
                {{ item.textToSpeech }}
              </span>
            </span>
          </template>
          <template v-slot:[`item.actions`]="{ item }">
            <Actions @reload="load" :data="item" :key="item.id"/>
          </template>
        </v-data-table>
      </v-card>
      <div v-else>
        <v-card v-for="item in items" :key="item.id" class="mb-4">
          <v-card-title>
            {{ item.name }}
            <v-spacer></v-spacer>
            <v-btn :disabled="!canWrite(item.id)" icon @click="openEditor(item)"><v-icon>mdi-pencil</v-icon></v-btn>
          </v-card-title>
          <v-card-text>
            <div><strong>{{ $t('announce.columns.pin') }}:</strong>&nbsp;{{ item.pin }}</div>
            <div><strong>{{ $t('announce.columns.pitch') }}:</strong>&nbsp;{{ item.pitch }}</div>
            <div><strong>{{ $t('announce.columns.speed') }}:</strong>&nbsp;{{ item.speed ? `x${item.speed}` : '' }}</div>
            <div><strong>{{ $t('announce.columns.volumeGainDb') }}:</strong>&nbsp;{{ (item.volumeGainDb !== '' && item.volumeGainDb !== null) ? `${item.volumeGainDb} DB` : '' }}</div>
            <div><strong>{{ $t('announce.columns.ttsLanguage') }}:</strong>&nbsp;{{ item.ttsLanguage }}</div>
            <div><strong>{{ $t('announce.columns.textType') }}:</strong>&nbsp;{{ item.textType ? $t(`announce.columnRenderer.channelTypes.${item.textType}`) : '' }}</div>
            <div><strong>{{ $t('announce.columns.textToSpeech') }}</strong><br />{{ item.textToSpeech }}</div>
          </v-card-text>
          <v-card-actions class="pl-4">
            <Actions @reload="load" :data="item" :key="item.id"/>
          </v-card-actions>
        </v-card>
        <Editor v-if="showEditor" :show="showEditor" :data="editorItem" @close="showEditor = false" @updated="onEditorUpdated" />
      </div>
    </v-flex>
  </v-container>
</template>

<script>
import Actions from './actions'
import Editor from './editor'
import { mapGetters, mapActions } from 'vuex'
import copyToClipboard from '@/helpers/copyToClipboard'

export default {
  name: 'announcements',
  components: { Actions, Editor },
  data () {
    return {
      loading: false,
      pagination: {},
      defaultItemPerPage: 10,
      footerProps: {
        'items-per-page-options': [5, 10, 15, 18, 20, 25, 50, 100],
        pageText: this.$t('announce.footerProps.pageText'),
        'items-per-page-text': this.$t('announce.footerProps.total')
      },
      copyButtonDisabled: {},
      copyButtonText: {},
      showEditor: false,
      editorItem: null
    }
  },
  watch: {
    async pagination () {
      this.loading = true
      await this.setPage(this.pagination?.page)
      await this.setLimitPerPage(this.pagination?.itemsPerPage)
      await this.load()
      await this.initSearch(this.$t('announce.searchPlaceholder'))
      this.loading = false
    },
    async searchText () {
      await this.load()
    }
  },
  mounted () {
    this.load()
    this.getTTSVoiceTypes()
  },
  computed: {
    ...mapGetters({
      items: 'announcements/items',
      announcementPagination: 'announcements/pagination',
      searchText: 'search/searchText',
      canWriteAll: 'permissions/announceWrite',
      canWriteFor: 'permissions/announceWriteFor'
    }),
    headers () {
      return [
        { text: this.$t('announce.columns.name'), value: 'name', align: 'center' },
        { text: this.$t('announce.columns.pin'), value: 'pin', align: 'center' },
        { text: this.$t('announce.columns.textToSpeech'), value: 'textToSpeech' },
        { text: this.$t('announce.columns.pitch'), value: 'pitch', align: 'center' },
        { text: this.$t('announce.columns.speed'), value: 'speed', align: 'center' },
        { text: this.$t('announce.columns.volumeGainDb'), value: 'volumeGainDb', align: 'center' },
        { text: this.$t('announce.columns.ttsLanguage'), value: 'ttsLanguage', align: 'center' },
        { text: this.$t('announce.columns.textType'), value: 'textType', align: 'center' },
        { text: this.$t('announce.columns.actions'), value: 'actions', width: this.$vuetify.breakpoint.mobile ? '130px' : '420px' }
      ]
    },
    TTSTextLength () {
      return this.$vuetify.breakpoint.smAndDown ? 30 : 100
    }
  },
  methods: {
    ...mapActions({
      load: 'announcements/load',
      setPage: 'announcements/setPage',
      setLimitPerPage: 'announcements/setLimitPerPage',
      initSearch: 'search/init',
      getTTSVoiceTypes: 'announcements/getTTSVoiceTypes'
    }),
    copy (text, id) {
      copyToClipboard(text)
      this.$set(this.copyButtonText, id, this.$t('announce.columnRenderer.copied'))
      this.$set(this.copyButtonDisabled, id, true)
      setTimeout(() => {
        this.$set(this.copyButtonText, id, this.$t('announce.columnRenderer.copy'))
        this.$set(this.copyButtonDisabled, id, false)
      }, 1500)
    },
    canWrite (announcementId) {
      return this.canWriteAll || this.canWriteFor(announcementId)
    },
    openEditor (item) {
      if (this.canWrite(item.id)) {
        this.editorItem = item
        this.showEditor = true
      }
    },
    onEditorUpdated () {
      this.showEditor = false
      this.load()
    }
  }
}
</script>

<style scoped>
.center {
  display: block;
  margin-left: auto;
  margin-right: auto;
  width: 50%;
}
</style>

<style>
.theme--light.v-data-table.stripped-table tbody tr:nth-of-type(even):not(.v-row-group__header) {
  background-color: rgba(0, 0, 0, .03);
}
.theme--dark.v-data-table.stripped-table tbody tr:nth-of-type(even):not(.v-row-group__header) {
  background-color: rgba(0, 0, 0, .5);
}
</style>
